import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import {
    BrowserView,
    CatalogObject,
    CatalogObjectSelection,
    CatalogObjectTypes,
    JobSite,
    ObjectBrowserSelectionTexts,
    ObjectMenuOption,
    ResultPage,
    SubMenuOption,
} from '@mirage-projects/cw-models';

import { GridColumn } from '../../models';
import { NavigationButton } from '../../models/navigation-button';
import { PaginationData } from '../../models/pagination-data';
import { ObjectBrowserService } from './object-browser.service';
import { ObjectBrowserSelectionService } from './selection.service';

export type gridStyleIcon = 'menu' | 'service-menu' | 'storage';

export interface MainButton {
    id: string;
    label: string;
    children?: MainButton[];
}

/**
 * Component that shows the layer/projects in the system, with filtering and pagination
 */
@Component({
    selector: 'cw-object-browser',
    templateUrl: 'object-browser.component.html',
    styleUrls: ['./object-browser.component.scss'],
    standalone: false,
})
export class ObjectBrowserComponent implements OnInit, OnChanges, OnDestroy {
    /** Views that can be selected in the Object Browser (For example: grid, list, detail, map...).
     * By default, the first one will be selected. Each view will have an array of filters,
     * since each view can show different filters. */
    @Input() browserViews!: BrowserView[];
    /** Columns for grid data */
    @Input() columnDataList: GridColumn[] = [];
    /** Pagination data */
    @Input() pagination: PaginationData = {
        currentPage: 1,
        pageSize: 20,
        totalElements: 0,
    };
    /** Data to render in the form of cards */
    @Input() data: ResultPage<CatalogObjectSelection> | null = null;
    /** Enables or disables multiple card selection */
    @Input() isMultiSelection = false;
    /** Flag that enables showing a loading animation. Useful when data is loading when changing pages */
    @Input() loading = false;
    /** If this entry has value, we display a yellow button at the top right with this text.
     * Clicking on this button will emit the event mainButtonAction*/
    @Input() mainButton?: MainButton;
    //  TODO: think about remove the general checkbox setting and include it in the card model
    /** Method to externally solve if the selection checkbox should be shown or not */
    @Input() shouldShowCheckboxes?: (...options: any) => boolean;
    /** Enable reload page from the parent component */
    @Input() reloadPage!: Subject<number>;
    /** Type of data that the component will work with */
    @Input() dataType!: CatalogObjectTypes;
    /**if this entry is present, we display a blue button icon at the top right */
    @Input() secondaryButton?: NavigationButton;
    /**if this entry is present, we show the custom tooltip for the refresh button*/
    @Input() refreshButtonTooltip = 'OBJECT_BROWSER.REFRESH';

    @Input() footerSelectionResources: ObjectBrowserSelectionTexts = {
        selectedElementsText: '',
        showSelectedText: '',
        showSelectedsText: '',
    };
    /** Enables shows or hide the selected elements filter from the parent component */
    @Input() showSelectedElementsFilter!: Subject<boolean>;

    /** Default selected view */
    @Input() selectedView: BrowserView | undefined;

    /** Emits when an element has been selected */
    @Output() elementSelect: EventEmitter<CatalogObjectSelection> = new EventEmitter<CatalogObjectSelection>();
    /** Emits when a new page has been selected */
    @Output() pageChange = new EventEmitter<number>();
    /** Emits when the internal filter has changed and data is requested */
    @Output() filterChange = new EventEmitter<Map<string, string>>();
    /** Emits when the internal filter has changed and data is requested */
    @Output() dataRefresh = new EventEmitter<void>();
    /** Emits if the selected view changes */
    @Output() selectedViewChange = new EventEmitter<BrowserView>();
    /** Is emitted when the main button is clicked */
    @Output() mainButtonAction = new EventEmitter<string>();
    /** Is emitted when a menu option is clicked */
    @Output() menuOptionClick = new EventEmitter<ObjectMenuOption>();
    /** Is emitted when a card is clicked */
    @Output() objectClick = new EventEmitter<CatalogObjectSelection>();
    /** Is emitted when a link field is clicked */
    @Output() linkFieldClick = new EventEmitter<CatalogObjectSelection>();
    /** Event that fires when the delete button is clicked */
    @Output() delete = new EventEmitter<void>();
    /** Event that fires when the create project button is clicked */
    @Output() createProject = new EventEmitter<void>();

    selectedCount = 0;
    lastCardSelectedId: number | undefined;
    catalogObjects!: CatalogObject[];
    jobSiteList!: JobSite[];
    resetFilter$ = new Subject<void>();
    gridData: CatalogObjectSelection[] = [];
    hasToShowSelectedElementsOnly = false;

    private onDestroy$: Subject<void> = new Subject();

    constructor(
        private objectBrowserService: ObjectBrowserService,
        public selectionService: ObjectBrowserSelectionService
    ) {}

    ngOnInit(): void {
        this.initView();
        this.selectionService.resetSelection();
        if (this.reloadPage) {
            this.reloadPage.pipe(takeUntil(this.onDestroy$)).subscribe((page: number) => this.onCurrentPageChanged(page));
        }

        if (this.showSelectedElementsFilter) {
            this.showSelectedElementsFilter
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((showSelectedElements) => (this.hasToShowSelectedElementsOnly = showSelectedElements));
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.selectedView && changes['data'] && changes['data'].currentValue !== changes['data'].previousValue) {
            this.onChangeView();
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    /** @ignore */
    onElementSelected(objectSelection: CatalogObjectSelection): void {
        this.updateSelectedCards(objectSelection);

        if (this.hasToShowSelectedElementsOnly) {
            this.pagination.currentPage = this.objectBrowserService.getValidPage(this.pagination);
            this.data = this.objectBrowserService.getSelectedElements(this.pagination.currentPage, this.pagination.pageSize, this.dataType);
        }
        this.elementSelect.emit(objectSelection);
    }

    /** @ignore */
    filterChanged(filterData: Map<string, string>): void {
        this.pagination.currentPage = 1;
        this.filterChange.emit(filterData);
    }

    /** @ignore */
    onCurrentPageChanged(pageNumber: number): void {
        if (!pageNumber) {
            return;
        }
        this.pagination.currentPage = pageNumber;

        this.changeCurrentPage(pageNumber);
    }

    onChangeView(): void {
        if (this.isMapView()) {
            this.jobSiteList = [];
            this.data?.results?.forEach((elem) => {
                this.jobSiteList.push(elem.catalogObject as JobSite);
            });
        }
        if (this.isListView()) {
            this.catalogObjects = [];
            this.data?.results?.forEach((elem) => {
                this.catalogObjects.push(elem.catalogObject);
            });
        }
    }

    dataRefreshed(): void {
        if (!this.hasToShowSelectedElementsOnly) {
            this.dataRefresh.emit();
        }
    }

    /** @ignore */
    isDataLoading(): boolean {
        return this.loading;
    }

    /** @ignore */
    changeView(view: BrowserView): void {
        if (this.selectedView === view) {
            return;
        }

        this.selectedView = view;
        this.onChangeView();
        this.selectedViewChange.emit(this.selectedView);
    }

    isGridView(): boolean {
        return this.selectedView?.id === 'grid';
    }

    isListView(): boolean {
        return this.selectedView?.id === 'list';
    }

    isDetailView(): boolean {
        return this.selectedView?.id === 'detail';
    }

    isMapView(): boolean {
        return this.selectedView?.id === 'map';
    }

    onClickMainButton(idButton: string): void {
        this.mainButtonAction.emit(idButton);
    }

    onMenuOptionClick(option: SubMenuOption, obj: CatalogObject): void {
        this.menuOptionClick.emit({
            menuOption: option,
            catalogObject: obj,
        });
    }

    /**
     * Updates the list of selected objects (depending if there is multiselection or not)
     * and emits the objectClick event
     * @param obj the selected object
     */
    onObjectClicked(obj: CatalogObjectSelection): void {
        this.updateSelectedCards(obj);
        this.objectClick.emit(obj);
    }

    /**
     * Emits an objectClick event when a object has been selected in the map view
     * @param catalogObject the selected object
     */
    onObjectMapClicked(catalogObject: CatalogObject): void {
        this.objectClick.emit({ catalogObject, selected: true });
    }

    getLength(): number {
        const dataCount = this.data ? this.data.count : 0;
        return Math.ceil(dataCount / this.pagination.pageSize);
    }

    onLinkFieldClick(row: CatalogObject): void {
        const element = this.data?.results.find((result) => result.catalogObject.id === row.id);
        this.linkFieldClick.emit(element);
    }

    isElementSelectFilterShown(): boolean {
        return this.hasToShowSelectedElementsOnly || this.areCatalogObjectsSelected();
    }

    areCatalogObjectsSelected(): boolean {
        return this.selectionService.getNumberSelectedElements() > 0;
    }

    changeCurrentPage(page: number): void {
        if (!this.hasToShowSelectedElementsOnly) {
            this.pageChange.emit(page);
        } else {
            this.data = this.objectBrowserService.getSelectedElements(page, this.pagination.pageSize, this.dataType);
        }
    }

    /**
     * Shows all the catalog objects or only the selected ones depending the passed parameter
     * Also, when it only shows the selected ones, it disables the filters, because they not apply
     * at that moment
     * @param hasToShowSelectedElements Only show selected elements when it is true
     */
    onShowSelectedElementsChanged(hasToShowSelectedElements: boolean): void {
        this.hasToShowSelectedElementsOnly = hasToShowSelectedElements;
        this.pagination.currentPage = 1;

        if (!this.hasToShowSelectedElementsOnly) {
            this.changeCurrentPage(this.pagination.currentPage);
        } else {
            this.data = this.objectBrowserService.getSelectedElements(this.pagination.currentPage, this.pagination.pageSize, this.dataType);
        }
    }

    /**
     * When selection is cleared, the elements are unselected, and the first page is reloaded
     */
    onClearSelection(): void {
        this.hasToShowSelectedElementsOnly = false;
        this.selectionService.resetSelection();
        this.reloadPage.next(this.pagination.currentPage);
    }

    onDelete(): void {
        this.delete.emit();
    }

    onCreateProject(): void {
        this.createProject.emit();
    }

    /**
     * Updates the list of selected elements when the show only selected flag is enabled
     */
    onCheckedAllChanged(): void {
        if (!this.hasToShowSelectedElementsOnly) {
            return;
        }

        // add some delay to notify user there was a page change/update
        this.loading = true;
        setTimeout(() => {
            this.pagination.currentPage = this.objectBrowserService.getValidPage(this.pagination);
            this.data = this.objectBrowserService.getSelectedElements(this.pagination.currentPage, this.pagination.pageSize, this.dataType);

            this.loading = false;
        }, 200);
    }

    /**
     * Calls the external method to see if the checkbox should be shown, in case there is one defined
     * @param obj Current catalog object selection
     */
    hasToShowCheckbox(obj: CatalogObjectSelection): boolean {
        return this.shouldShowCheckboxes ? this.shouldShowCheckboxes(obj) : true;
    }

    private initView(): void {
        this.selectedView = this.selectedView ?? this.browserViews[0];
    }

    /**
     * Selects and unselects cards depending the selection mode (multiple or only one selection)
     * @param objectSelection object to add to the selection list
     */
    private updateSelectedCards(objectSelection: CatalogObjectSelection): void {
        this.objectBrowserService.updateCardSelection(objectSelection.catalogObject.id, objectSelection.selected, this.data);
        this.lastCardSelectedId = this.objectBrowserService.updateSelectedCards(
            objectSelection,
            this.data,
            this.isMultiSelection,
            this.lastCardSelectedId,
            this.dataType
        );
    }
}
